import Link from 'next/link';
import type { SessionData } from '@/src/server/session/session.helper';
import { logoutAction } from '@/src/server/actions/logout.action';
import { deriveRoleLevel, ROLE_LEVEL_LABELS, hasMultipleRoleLevels } from '@/src/lib/roles';
import { ExperiencedToggle } from '@/src/components/onboarding/experienced-toggle';

export function UserMenu({ session }: { session: SessionData }) {
  const initials = (session.displayName || session.userId || 'KK')
    .split(/\s+/)
    .map((p) => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();

  const roleLevel = deriveRoleLevel(session.roles);
  const multi = hasMultipleRoleLevels(session.roles);

  return (
    <details className="group relative">
      <summary
        aria-label="Benutzermenü öffnen"
        className="flex cursor-pointer list-none items-center gap-3 rounded-[var(--k-radius-md)] border border-[var(--k-color-border-subtle)] bg-[var(--k-color-surface)] px-2 py-1.5 shadow-[var(--k-shadow-xs)] transition duration-150 hover:border-[var(--k-color-border-default)] hover:bg-[var(--k-color-surface-muted)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--k-color-focus-ring)]"
      >
        <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-[var(--k-color-accent)] text-sm font-bold text-white">
          {initials}
        </span>
        <span className="hidden text-left 2xl:block">
          <span className="block text-sm font-semibold text-[var(--k-color-text-primary)]">
            {session.displayName || 'Benutzerkonto'}
          </span>
          <span className="block text-xs text-[var(--k-color-text-muted)]">
            {ROLE_LEVEL_LABELS[roleLevel]}
            {multi ? ' · Mehrere Rollen' : ''}
          </span>
        </span>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="hidden text-[var(--k-color-text-muted)] transition group-open:rotate-180 2xl:block"
          aria-hidden="true"
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </summary>
      <div className="absolute right-0 z-40 mt-2 w-72 overflow-hidden rounded-[var(--k-radius-md)] border border-[var(--k-color-border-subtle)] bg-[var(--k-color-surface)] shadow-[var(--k-shadow-lg)]">
        <div className="border-b border-[var(--k-color-border-subtle)] bg-[var(--k-color-surface-muted)] p-4">
          <p className="text-sm font-semibold text-[var(--k-color-text-primary)]">
            {session.displayName || 'Benutzerkonto'}
          </p>
          <p className="mt-0.5 text-xs text-[var(--k-color-text-muted)]">
            User-ID: {session.userId}
          </p>
          <p className="text-xs text-[var(--k-color-text-muted)]">
            Mandant: {session.activeTenantPublicId}
          </p>
          <p className="mt-1 text-xs text-[var(--k-color-text-muted)]">
            Rolle:{' '}
            <span className="font-semibold text-[var(--k-color-text-primary)]">
              {ROLE_LEVEL_LABELS[roleLevel]}
            </span>
            {multi ? (
              <span className="ml-1 rounded-full bg-[var(--k-color-surface)] px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-[var(--k-color-text-muted)] ring-1 ring-[var(--k-color-border-subtle)]">
                Mehrfachrolle
              </span>
            ) : null}
            <span
              role="status"
              className={`ml-1 inline-block rounded px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide ${
                session.hasTwoFactor
                  ? 'bg-[var(--k-color-success-soft)] text-[var(--k-color-success-strong)]'
                  : 'bg-[var(--k-color-warning-soft)] text-[var(--k-color-warning-strong)]'
              }`}
              aria-label={session.hasTwoFactor ? '2FA aktiv' : '2FA nicht aktiv'}
            >
              2FA {session.hasTwoFactor ? 'aktiv' : 'inaktiv'}
            </span>
          </p>
        </div>
        <div className="p-2">
          <Link
            href="/einstellungen/profil"
            className="flex items-center gap-2 rounded-[var(--k-radius-sm)] px-3 py-2 text-sm font-medium text-[var(--k-color-text-secondary)] transition hover:bg-[var(--k-color-surface-muted)] hover:text-[var(--k-color-text-primary)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--k-color-focus-ring)]"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <circle cx="12" cy="8" r="4" />
              <path d="M4 21a8 8 0 0 1 16 0" />
            </svg>
            Mein Profil
          </Link>
          {['admin', 'superuser'].includes(roleLevel) ? (
          <Link
            href="/einstellungen/benutzer-rollen"
            className="flex items-center gap-2 rounded-[var(--k-radius-sm)] px-3 py-2 text-sm font-medium text-[var(--k-color-text-secondary)] transition hover:bg-[var(--k-color-surface-muted)] hover:text-[var(--k-color-text-primary)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--k-color-focus-ring)]"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
              <circle cx="12" cy="7" r="4" />
            </svg>
            Benutzer &amp; Rollen
          </Link>
          ) : null}
          <Link
            href="/einstellungen/sicherheit"
            className="flex items-center gap-2 rounded-[var(--k-radius-sm)] px-3 py-2 text-sm font-medium text-[var(--k-color-text-secondary)] transition hover:bg-[var(--k-color-surface-muted)] hover:text-[var(--k-color-text-primary)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--k-color-focus-ring)]"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
            </svg>
            Sicherheit
          </Link>
          <div className="my-1 border-t border-[var(--k-color-border-subtle)]" />
          <ExperiencedToggle />
          <div className="my-1 border-t border-[var(--k-color-border-subtle)]" />
          <form action={logoutAction}>
            <button
              type="submit"
              className="flex w-full items-center gap-2 rounded-[var(--k-radius-sm)] px-3 py-2 text-left text-sm font-medium text-[var(--k-color-danger-strong)] transition hover:bg-[var(--k-color-danger-soft)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--k-color-focus-ring)]"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                <polyline points="16 17 21 12 16 7" />
                <line x1="21" y1="12" x2="9" y2="12" />
              </svg>
              Abmelden
            </button>
          </form>
        </div>
      </div>
    </details>
  );
}
